import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { useLanguage } from "@/contexts/LanguageContext";
import { db } from "@/lib/firebase";
import { collection, addDoc, getDocs, query, where, orderBy, serverTimestamp, Timestamp } from "firebase/firestore";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { useToast } from "@/hooks/use-toast";
import { formatCurrency } from "@/config/company.config";
import {
  ArrowLeft,
  Briefcase,
  Save,
  Scale,
  Calculator,
  DollarSign,
  Plane,
  FileSearch,
  FileText,
  Clock,
  Calendar,
  User,
} from "lucide-react";

interface Client {
  id: string;
  name: string;
  email: string;
  company?: string;
  type: string;
}

const PARTNER_TYPE_LABELS: Record<string, { en: string; fr: string }> = {
  'business-law': { en: 'Business Law', fr: 'Droit des Affaires' },
  'immigration-law': { en: 'Immigration Law', fr: 'Droit de l\'Immigration' },
  'notary': { en: 'Notary Services', fr: 'Services Notariaux' },
  'tax-advisor': { en: 'Tax Advisory', fr: 'Conseil Fiscal' },
  'cpa': { en: 'CPA/Accounting', fr: 'CPA/Comptabilité' },
};

const translations = {
  en: {
    title: "Create New Mandate",
    subtitle: "Create a new mandate for your client",
    back: "Back to Mandates",
    mandateDetails: "Mandate Details",
    mandateTitle: "Mandate Title",
    titlePlaceholder: "Enter mandate title",
    description: "Description",
    descriptionPlaceholder: "Describe the scope of work...",
    selectClient: "Select Client",
    noClients: "No clients available",
    addClientFirst: "Add a client first",
    mandateType: "Mandate Type",
    priority: "Priority",
    duration: "Duration (days)",
    estimatedHours: "Estimated Hours",
    hourlyRate: "Hourly Rate ($)",
    startDate: "Start Date",
    dueDate: "Due Date",
    totalFee: "Estimated Total Fee",
    legal: "Legal Services",
    tax: "Tax Advisory",
    financial: "Financial Analysis",
    immigration: "Immigration Law",
    notary: "Notary Services",
    accounting: "Accounting",
    due_diligence: "Due Diligence",
    other: "Other Services",
    low: "Low",
    medium: "Medium",
    high: "High",
    urgent: "Urgent",
    create: "Create Mandate",
    creating: "Creating...",
    success: "Mandate Created",
    successMessage: "Mandate has been created successfully",
    error: "Error",
    errorMessage: "Failed to create mandate",
    required: "This field is required",
    cancel: "Cancel",
  },
  fr: {
    title: "Créer un Nouveau Mandat",
    subtitle: "Créez un nouveau mandat pour votre client",
    back: "Retour aux Mandats",
    mandateDetails: "Détails du Mandat",
    mandateTitle: "Titre du Mandat",
    titlePlaceholder: "Entrez le titre du mandat",
    description: "Description",
    descriptionPlaceholder: "Décrivez l'étendue des travaux...",
    selectClient: "Sélectionner un Client",
    noClients: "Aucun client disponible",
    addClientFirst: "Ajoutez d'abord un client",
    mandateType: "Type de Mandat",
    priority: "Priorité",
    duration: "Durée (jours)",
    estimatedHours: "Heures Estimées",
    hourlyRate: "Taux Horaire ($)",
    startDate: "Date de Début",
    dueDate: "Date d'Échéance",
    totalFee: "Honoraires Estimés",
    legal: "Services Juridiques",
    tax: "Conseil Fiscal",
    financial: "Analyse Financière",
    immigration: "Droit de l'Immigration",
    notary: "Services Notariaux",
    accounting: "Comptabilité",
    due_diligence: "Due Diligence",
    other: "Autres Services",
    low: "Basse",
    medium: "Moyenne",
    high: "Haute",
    urgent: "Urgente",
    create: "Créer le Mandat",
    creating: "Création en cours...",
    success: "Mandat Créé",
    successMessage: "Le mandat a été créé avec succès",
    error: "Erreur",
    errorMessage: "Échec de la création du mandat",
    required: "Ce champ est requis",
    cancel: "Annuler",
  },
};

export default function PartnerNewMandate() {
  const { partnerType } = useParams<{ partnerType: string }>();
  const { user } = useAuth();
  const { language } = useLanguage();
  const navigate = useNavigate();
  const { toast } = useToast();
  const t = translations[language as keyof typeof translations] || translations.en;

  const [clients, setClients] = useState<Client[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isLoadingClients, setIsLoadingClients] = useState(true);

  const [formData, setFormData] = useState({
    title: "",
    description: "",
    clientId: "",
    type: partnerType?.includes('tax') || partnerType?.includes('cpa') ? 'tax' : 
          partnerType?.includes('immigration') ? 'immigration' : 
          partnerType?.includes('notary') ? 'notary' : 'legal',
    priority: "medium" as 'low' | 'medium' | 'high' | 'urgent',
    duration: 30,
    estimatedHours: 40,
    hourlyRate: 250,
    startDate: new Date().toISOString().split('T')[0],
  });
  const [errors, setErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    if (user) {
      loadClients();
    }
  }, [user]);

  const loadClients = async () => {
    if (!user) return;
    setIsLoadingClients(true);
    try {
      const clientsRef = collection(db, "partnerClients");
      const clientsQuery = query(
        clientsRef,
        where("partnerId", "==", user.email),
        orderBy("createdAt", "desc")
      );
      const snapshot = await getDocs(clientsQuery);
      const clientsData = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      })) as Client[];
      setClients(clientsData);
    } catch (error) {
      console.error("Error loading clients:", error);
    } finally {
      setIsLoadingClients(false);
    }
  };

  const calculateDueDate = () => {
    const startDate = new Date(formData.startDate);
    const dueDate = new Date(startDate);
    dueDate.setDate(dueDate.getDate() + formData.duration);
    return dueDate.toISOString().split('T')[0];
  };

  const calculateTotalFee = () => {
    return formData.estimatedHours * formData.hourlyRate;
  };

  const validateForm = () => {
    const newErrors: Record<string, string> = {};
    if (!formData.title.trim()) newErrors.title = t.required;
    if (!formData.clientId) newErrors.clientId = t.required;
    if (!formData.type) newErrors.type = t.required;
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validateForm() || !user) return;

    setIsSubmitting(true);

    try {
      const selectedClient = clients.find(c => c.id === formData.clientId);
      const startDate = new Date(formData.startDate);
      const dueDate = new Date(startDate);
      dueDate.setDate(dueDate.getDate() + formData.duration);

      await addDoc(collection(db, "partnerMandates"), {
        partnerId: user.email,
        partnerType: partnerType,
        clientId: formData.clientId,
        clientName: selectedClient?.name || '',
        title: formData.title,
        description: formData.description,
        type: formData.type,
        status: 'pending',
        priority: formData.priority,
        startDate: Timestamp.fromDate(startDate),
        dueDate: Timestamp.fromDate(dueDate),
        estimatedHours: formData.estimatedHours,
        loggedHours: 0,
        hourlyRate: formData.hourlyRate,
        totalFee: calculateTotalFee(),
        progress: 0,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      });

      toast({
        title: t.success,
        description: t.successMessage,
      });

      navigate(`/internal-partners/${partnerType}/mandates`);
    } catch (error) {
      console.error("Error creating mandate:", error);
      toast({
        title: t.error,
        description: t.errorMessage,
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const partnerLabel = PARTNER_TYPE_LABELS[partnerType || '']?.[language as 'en' | 'fr'] || partnerType;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button 
          variant="ghost" 
          size="icon"
          onClick={() => navigate(`/internal-partners/${partnerType}/mandates`)}
        >
          <ArrowLeft className="h-5 w-5" />
        </Button>
        <div>
          <h1 className="text-3xl font-bold tracking-tight">{t.title}</h1>
          <p className="text-muted-foreground mt-1">
            {t.subtitle} - <Badge variant="outline">{partnerLabel}</Badge>
          </p>
        </div>
      </div>

      {/* Form */}
      <form onSubmit={handleSubmit}>
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Briefcase className="h-5 w-5" />
              {t.mandateDetails}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Title */}
            <div className="space-y-2">
              <Label htmlFor="title">{t.mandateTitle} *</Label>
              <Input
                id="title"
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                placeholder={t.titlePlaceholder}
                className={errors.title ? 'border-destructive' : ''}
              />
              {errors.title && <p className="text-sm text-destructive">{errors.title}</p>}
            </div>

            {/* Description */}
            <div className="space-y-2">
              <Label htmlFor="description">{t.description}</Label>
              <Textarea
                id="description"
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder={t.descriptionPlaceholder}
                rows={4}
              />
            </div>

            {/* Client Selection */}
            <div className="space-y-2">
              <Label>{t.selectClient} *</Label>
              <Select 
                value={formData.clientId} 
                onValueChange={(value) => setFormData({ ...formData, clientId: value })}
              >
                <SelectTrigger className={errors.clientId ? 'border-destructive' : ''}>
                  <User className="h-4 w-4 mr-2 text-muted-foreground" />
                  <SelectValue placeholder={t.selectClient} />
                </SelectTrigger>
                <SelectContent>
                  {isLoadingClients ? (
                    <SelectItem value="loading" disabled>Loading...</SelectItem>
                  ) : clients.length > 0 ? (
                    clients.map((client) => (
                      <SelectItem key={client.id} value={client.id}>
                        {client.name} {client.company && `(${client.company})`}
                      </SelectItem>
                    ))
                  ) : (
                    <SelectItem value="none" disabled>{t.noClients}</SelectItem>
                  )}
                </SelectContent>
              </Select>
              {errors.clientId && <p className="text-sm text-destructive">{errors.clientId}</p>}
            </div>

            {/* Type and Priority */}
            <div className="grid md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>{t.mandateType} *</Label>
                <Select 
                  value={formData.type} 
                  onValueChange={(value) => setFormData({ ...formData, type: value })}
                >
                  <SelectTrigger className={errors.type ? 'border-destructive' : ''}>
                    <SelectValue placeholder={t.mandateType} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="legal">{t.legal}</SelectItem>
                    <SelectItem value="tax">{t.tax}</SelectItem>
                    <SelectItem value="financial">{t.financial}</SelectItem>
                    <SelectItem value="immigration">{t.immigration}</SelectItem>
                    <SelectItem value="notary">{t.notary}</SelectItem>
                    <SelectItem value="accounting">{t.accounting}</SelectItem>
                    <SelectItem value="due_diligence">{t.due_diligence}</SelectItem>
                    <SelectItem value="other">{t.other}</SelectItem>
                  </SelectContent>
                </Select>
                {errors.type && <p className="text-sm text-destructive">{errors.type}</p>}
              </div>

              <div className="space-y-2">
                <Label>{t.priority}</Label>
                <Select 
                  value={formData.priority} 
                  onValueChange={(value: 'low' | 'medium' | 'high' | 'urgent') => 
                    setFormData({ ...formData, priority: value })
                  }
                >
                  <SelectTrigger>
                    <SelectValue placeholder={t.priority} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="low">{t.low}</SelectItem>
                    <SelectItem value="medium">{t.medium}</SelectItem>
                    <SelectItem value="high">{t.high}</SelectItem>
                    <SelectItem value="urgent">{t.urgent}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Dates */}
            <div className="grid md:grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label htmlFor="startDate">{t.startDate}</Label>
                <Input
                  id="startDate"
                  type="date"
                  value={formData.startDate}
                  onChange={(e) => setFormData({ ...formData, startDate: e.target.value })}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="duration">{t.duration}</Label>
                <Input
                  id="duration"
                  type="number"
                  value={formData.duration}
                  onChange={(e) => setFormData({ ...formData, duration: parseInt(e.target.value) || 30 })}
                  min={1}
                />
              </div>
              <div className="space-y-2">
                <Label>{t.dueDate}</Label>
                <div className="flex items-center h-10 px-3 rounded-md border bg-muted/50">
                  <Calendar className="h-4 w-4 mr-2 text-muted-foreground" />
                  <span className="text-sm">{calculateDueDate()}</span>
                </div>
              </div>
            </div>

            {/* Hours and Rate */}
            <div className="grid md:grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label htmlFor="estimatedHours">{t.estimatedHours}</Label>
                <Input
                  id="estimatedHours"
                  type="number"
                  value={formData.estimatedHours}
                  onChange={(e) => setFormData({ ...formData, estimatedHours: parseInt(e.target.value) || 0 })}
                  min={0}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="hourlyRate">{t.hourlyRate}</Label>
                <Input
                  id="hourlyRate"
                  type="number"
                  value={formData.hourlyRate}
                  onChange={(e) => setFormData({ ...formData, hourlyRate: parseInt(e.target.value) || 0 })}
                  min={0}
                />
              </div>
              <div className="space-y-2">
                <Label>{t.totalFee}</Label>
                <div className="flex items-center h-10 px-3 rounded-md border bg-muted/50">
                  <DollarSign className="h-4 w-4 mr-2 text-muted-foreground" />
                  <span className="text-sm font-semibold">{formatCurrency(calculateTotalFee())}</span>
                </div>
              </div>
            </div>

            {/* Actions */}
            <div className="flex justify-end gap-4 pt-4 border-t">
              <Button 
                type="button" 
                variant="outline"
                onClick={() => navigate(`/internal-partners/${partnerType}/mandates`)}
              >
                {t.cancel}
              </Button>
              <Button type="submit" disabled={isSubmitting}>
                {isSubmitting ? (
                  <>
                    <Clock className="mr-2 h-4 w-4 animate-spin" />
                    {t.creating}
                  </>
                ) : (
                  <>
                    <Save className="mr-2 h-4 w-4" />
                    {t.create}
                  </>
                )}
              </Button>
            </div>
          </CardContent>
        </Card>
      </form>
    </div>
  );
}
